import React, { useEffect } from 'react';

/**
 * Per-role PWA metadata. Keeping this map in one place makes it easy to keep
 * the manifest, the Apple touch icon and the iOS status-bar theme colour in
 * sync when the user navigates between role landing pages.
 */
const ROLE_META = {
  '/manifest.json':         { themeColor: '#FF4500', icon192: '/icon-main-192.png',    icon512: '/icon-main-512.png',    appleTitle: 'LUGGAGEFREEJOURNEY' },
  '/manifest-partner.json': { themeColor: '#0A1F44', icon192: '/icon-partner-192.png', icon512: '/icon-partner-512.png', appleTitle: 'LUGGAGEFREEJOURNEY' },
  '/manifest-agent.json':   { themeColor: '#0D9488', icon192: '/icon-agent-192.png',   icon512: '/icon-agent-512.png',   appleTitle: 'LUGGAGEFREEJOURNEY' },
  '/manifest-admin.json':   { themeColor: '#000000', icon192: '/icon-main-192.png',    icon512: '/icon-main-512.png',    appleTitle: 'LUGGAGEFREEJOURNEY' },
};

const ensureLink = (rel, attrs = {}) => {
  if (typeof document === 'undefined') return null;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const ensureMeta = (nameOrProp, value, useName = true) => {
  if (typeof document === 'undefined') return null;
  const selectorAttr = useName ? 'name' : 'property';
  let el = document.querySelector(`meta[${selectorAttr}="${nameOrProp}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(selectorAttr, nameOrProp);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  return el;
};

export const setActiveManifest = (manifest = '/manifest.json') => {
  const meta = ROLE_META[manifest] || ROLE_META['/manifest.json'];
  ensureLink('manifest', { href: manifest });
  ensureLink('apple-touch-icon', { href: meta.icon192 });
  ensureMeta('theme-color', meta.themeColor);
  ensureMeta('apple-mobile-web-app-title', meta.appleTitle);
};

export const manifestForPath = (pathname = '/') => {
  if (pathname.startsWith('/partner')) return '/manifest-partner.json';
  if (pathname.startsWith('/agent')) return '/manifest-agent.json';
  if (pathname.startsWith('/admin')) return '/manifest-admin.json';
  return '/manifest.json';
};

/**
 * Install-app affordance intentionally removed at product's request. The app
 * now runs purely as a browser experience on both Android and iOS. We still
 * swap the per-role manifest (so service-worker scope + push subscriptions
 * stay correct) and we suppress Chrome's default `beforeinstallprompt` UI so
 * users don't get nudged into installing via the browser's built-in banner.
 * All existing callers can keep rendering <InstallAppButton /> — it just
 * returns null.
 */
export const InstallAppButton = ({ manifest = '/manifest.json' }) => {
  useEffect(() => {
    setActiveManifest(manifest);
  }, [manifest]);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  return null;
};

export default InstallAppButton;
